<!--
  -  Author: Raul Perusquia <raul@inikoo.com>
  -  Created: Sun, 30 Oct 2022 15:27:23 Greenwich Mean Time, Kuala Lumpur, Malaysia
  -  Copyright (c) 2022, Raul A Perusquia Flores
  -->

<script lang="ts">
import { faSpinnerThird } from "@fad/faSpinnerThird";
import { library } from "@fortawesome/fontawesome-svg-core"
import { faPencil } from "@fal/faPencil";import { faDownload } from "@fal/faDownload";import { faPlus as falPlus } from "@fal/faPlus";
import { faArrowLeft } from "@far/faArrowLeft";import { faTrashAlt } from "@far/faTrashAlt";import { faTimes } from "@far/faTimes";
import { faPlus } from "@fas/faPlus";import { faSave } from "@fas/faSave";import { faUpload } from "@fas/faUpload";import { faTrashUndoAlt } from "@fas/faTrashUndoAlt";import { faMinus } from "@fas/faMinus";

// Registered once per module load (plain <script>), not per Button instance
// (<script setup> re-runs its whole body for every mounted component).
library.add(faPlus, faSave, faUpload, faDownload, falPlus, faArrowLeft, faPencil, faTrashAlt, faSpinnerThird, faTrashUndoAlt, faTimes, faMinus)
</script>

<!-- Script: Setup -->
<script setup lang="ts">
import { ctrans } from "@/Composables/useTrans"
import type { IconDefinition } from "@fal"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { Icon } from "@/types/Utils/Icon"

const props = withDefaults(defineProps<{
    style?: string | object
    size?: string
    icon?: Icon | string | string[] | IconDefinition
    iconRight?: Icon | string | string[] | IconDefinition
    action?: string
    label?: string
    hide_label?: boolean
    full?: boolean
    capitalize?: boolean
    tooltip?: string
    loading?: boolean
    type?: string
    nativeType?: "button" | "submit" | "reset"
    disabled?: boolean
    noHover?: boolean
    injectStyle?: object | null
    iconRightRotation?: number | string
    iconRotation?: number | string
}>(), {
    size: "m",
    capitalize: false,
    loading: false,
    nativeType: "button"
})


let styleClass: string
let sizeClass = ""

// Styling the Button depends on the 'style' props
if (props.type == "primary" || props.type == "create" || props.type == "save" || props.type == "upload" || props.style == "primary" || props.style == "create" || props.style == "save" || props.style == "upload") {
    styleClass = `buttonPrimary ${props.injectStyle ? "buttonPrimary-has-inject-style" : ""}`
} else if (props.type == "secondary" || props.style == "secondary") {
    styleClass = "buttonSecondary"
} else if (props.type == "tertiary" || props.style == "tertiary") styleClass = "bg-transparent border border-gray-300 text-gray-700 hover:bg-gray-200/70 disabled:bg-gray-200/70"
else if (props.type == "transparent" || props.style == "transparent") styleClass = `bg-transparent ${props.noHover ? "" : "hover:bg-gray-200/70"} text-gray-700  disabled:bg-gray-200/70`
else if (props.type == "exit" || props.style == "exit" || props.style == "exitEdit" || props.type == "edit" || props.style == "edit") styleClass = "buttonEdit"
else if (props.type == "dashed" || props.style == "dashed") styleClass = "bg-transparent border border-dashed border-gray-400 text-gray-700 hover:bg-black/10"
else if (props.type == "rainbow" || props.style == "rainbow") styleClass = "bg-indigo-500 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
else if (props.style == "delete" || props.style == "negative" || props.style == "cancel" || props.type == "delete" || props.type == "negative" || props.type == "cancel") styleClass = "border border-red-400 text-red-500 hover:text-red-600 hover:bg-red-100 disabled:border-red-200 disabled:bg-red-100 disabled:text-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
else if (props.style == "positive" || props.type == "positive") styleClass = `${props.noHover ? "cursor-default" : "hover:text-green-800 hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2"} border border-green-500 text-green-600 disabled:bg-green-200/75 `
else if (props.style == "warning" || props.type == "warning") styleClass = "border border-amber-500 text-amber-600 hover:text-amber-800 bg-amber-10 hover:bg-amber-200 disabled:bg-amber-300/75 focus:outline-none focus:ring-2 focus:ring-amber-600 focus:ring-offset-2"
else if (props.style == "yellow" || props.type == "yellow") styleClass = "bg-yellow-400 hover:bg-yellow-500 border border-yellow-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
else if (props.style == "white" || props.type == "white") styleClass = "bg-white hover:bg-gray-300 text-gray-600"
else if (props.style == "red" || props.type == "red") styleClass = "bg-red-500 hover:bg-red-600 border border-red-500 hover:border-red-600 text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:text-red-300 disabled:bg-red-600"
else if (props.style == "green" || props.type == "green") styleClass = `bg-green-500 ${props.noHover ? "" : "hover:bg-green-600"} border border-green-600  text-white focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2`
else if (props.style == "gray" || props.type == "gray") styleClass = "bg-gray-300 hover:bg-gray-400 border border-gray-500 text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
else if (props.style == "black" || props.type == "black") styleClass = "bg-gray-800 hover:bg-gray-700 disabled:bg-gray-600 border border-gray-900 text-white focus:outline-none focus:ring-2 focus:ring-gray-800 focus:ring-offset-2"
else if (props.style == "indigo" || props.type == "indigo") styleClass = "bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-800 border border-indigo-500 text-teal-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
else if (props.style == "red_outline" || props.type == "red_outline") styleClass = "border border-red-400 text-red-500 hover:text-red-600 hover:bg-red-100 disabled:border-red-200 disabled:bg-red-100 disabled:text-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
else if (props.style == "disabled" || props.type == "disabled") styleClass = "cursor-not-allowed border border-gray-300 bg-transparent text-gray-700 hover:bg-gray-200/70 disabled:cursor-not-allowed disabled:opacity-70"
else styleClass = `buttonPrimary ${props.injectStyle ? "buttonPrimary-has-inject-style" : ""}`

if (props.style == "white-w-outline" || props.type == "white-w-outline") {
    styleClass = "bg-white hover:bg-gray-200/70 border border-gray-300  text-gray-600 disabled:bg-gray-300 disabled:border-gray-400"
}
if (props.style == "blue-bk-outline" || props.type == "blue-bk-outline") {
    styleClass = "bg-blue-400 hover:bg-blue-400/70 border border-gray-400 disabled:bg-gray-400 disabled:border-gray-800"
}
if (props.style == "white-r-outline" || props.type == "white-r-outline") {
    styleClass = "bg-white hover:bg-red-200 border border-red-300 text-red-500 hover:text-red-600  disabled:border-red-200 disabled:bg-gray-300 disabled:text-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 "
}
if (props.style == "red-r-outline" || props.type == "red-r-outline") {
    styleClass = "bg-red-500 hover:bg-red-600 border border-red-600 text-white disabled:border-red-200 disabled:bg-red-200 disabled:text-gray-300"
}


// Styling depends on the 'size' props
switch (props.size) {
    case "xxs":
        sizeClass = "rounded-sm px-1 py-0.5 md:px-2 md:py-1 text-xxs"
        break
    case "xs":
        sizeClass = "rounded px-1.5 md:px-2.5 py-1 md:py-1.5 text-xs"
        break
    case "sm":
    case "s":
        sizeClass = "rounded px-2 md:px-3 py-[5px] md:py-[7px] text-sm"
        break
    case "m":
    case "md":
        sizeClass = "rounded-md px-3 md:px-4 py-[6px] md:py-[9px] text-sm"
        break
    case "l":
    case "lg":
        sizeClass = "rounded-md px-4 md:px-[18px] py-[9px] md:py-[11px] text-base"
        break
    case "xl":
        sizeClass = "rounded-md px-[18px] md:px-6 py-[11px] md:py-[13px] text-lg"
        break
}

// Auto add label for several conditions
const getActionLabel = (label: string | undefined) => {
    if (label) {
        return label
    } else {
        switch (props.style || props.type) {
            case "edit":
                return null
            case "save":
                return ctrans("Save")
            case "create":
                return ctrans("Create")
            case "exit":
                return ctrans("Exit")
            case "exitEdit":
                return ctrans("Exit edit")
            case "cancel":
                return ctrans("Cancel")
            case "delete":
                return ctrans("Delete")
            case "clearMulti":
                return ctrans("Clear")
            default:
                return ""
        }
    }
}

// Auto-add icon for several conditions
const getActionIcon = (icon: any) => {
    if (icon === false) {
        return null
    } else if (icon) {
        return icon
    } else {
        switch (props.style || props.type) {
            case "edit":
                return ["fal", "fa-pencil"]
            case "save":
                return ["fas", "fa-save"]
            case "cancel":
            case "exit":
            case "exitEdit":
                return ["far", "fa-arrow-left"]
            case "create":
                return ["fas", "fa-plus"]
            case "delete":
                return ["far", "fa-trash-alt"]
            case "withMulti":
                return ["far", "fa-border-all"]
            case "upload":
                return ["fas", "fa-upload"]
            default:
                return null
        }
    }
}

// import { onRenderTriggered } from 'vue'

// onRenderTriggered((event) => {
//   console.log('Render triggered by:', event)
// })

</script>

<template>
    <button
        :style="injectStyle ?? {}"
        :type="nativeType"
        class="leading-4 inline-flex items-center gap-x-2 font-medium focus:outline-none disabled:cursor-not-allowed"
        :class="[
	            full ? 'w-full justify-center' : 'xmin-w-max',
	            styleClass,
	            sizeClass
	        ]"

        :disabled="loading || disabled || style == 'disabled' || type == 'disabled'"
        :aria-label="!getActionLabel(label) || hide_label ? (tooltip || getActionLabel(label) || label || undefined) : undefined"
        v-tooltip="tooltip ?? undefined"
    >
        <slot :loading>
            <slot name="loading">
                <FontAwesomeIcon v-if="!(!icon && iconRight) && loading" icon="fad fa-spinner-third" class="animate-spin" fixed-width aria-hidden="true" />
            </slot>
            <slot name="icon" v-if="!loading">
                <FontAwesomeIcon v-if="getActionIcon(icon)" :icon="getActionIcon(icon)" :rotation="iconRotation" fixed-width class="" aria-hidden="true" />
            </slot>
            <slot name="label"></slot>
            <span v-if="getActionLabel(label) && !hide_label" class="leading-none tabular-nums" :class="{'capitalize': capitalize}">{{ getActionLabel(label) }}</span>
            <slot name="iconRight">
                <FontAwesomeIcon v-if="!getActionIcon(icon) && loading && iconRight" icon="fad fa-spinner-third" class="animate-spin" fixed-width aria-hidden="true" />
                <FontAwesomeIcon v-else-if="iconRight" :icon="getActionIcon(iconRight)" :rotation="iconRightRotation" fixed-width class="" aria-hidden="true" />
            </slot>
        </slot>
    </button>
</template>

<style lang="scss">

.buttonPrimary {
    background-color: var(--theme-color-4) !important;
    color: var(--theme-color-5) !important;
    border: 1px solid color-mix(in srgb, var(--theme-color-4) 80%, black);

    &:hover {
        background-color: color-mix(in srgb, var(--theme-color-4) 85%, black) !important;
    }

    &:focus {
        box-shadow: 0 0 0 2px var(--theme-color-4) !important;
    }

    &:disabled {
        background-color: color-mix(in srgb, var(--theme-color-4) 70%, grey) !important;
    }
}

.buttonPrimary-has-inject-style {
    border-color: color-mix(in srgb, transparent 80%, black);
}

.buttonSecondary {
    // Primary but less opacity
    background-color: color-mix(in srgb, var(--theme-color-4) 14%, transparent) !important;
    border: 1px solid color-mix(in srgb, var(--theme-color-4) 53%, transparent);
    color: color-mix(in srgb, var(--theme-color-4) 50%, black) !important;

    &:hover {
        background-color: color-mix(in srgb, color-mix(in srgb, var(--theme-color-4) 13%, transparent) 90%, black) !important;
    }

    &:focus {
        box-shadow: 0 0 0 2px var(--theme-color-4) !important;
    }

    &:disabled {
        background-color: color-mix(in srgb, color-mix(in srgb, var(--theme-color-4) 13%, transparent) 80%, black) !important;
    }
}

.buttonEdit {
    // Primary but less opacity
    @apply bg-transparent text-xl !important;

    color: var(--theme-color-4) !important;

    &:hover {
        background-color: color-mix(in srgb, var(--theme-color-4) 20%, transparent) !important;
    }

    &:disabled {
        background-color: color-mix(in srgb, var(--theme-color-4) 30%, 30% grey) !important;
    }
}
</style>
